import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import CustomeButton from "~/components/Button";
import { FileUpload } from "primereact/fileupload";
import Attachments from "./Attachments";
import { getName } from "country-list";
import { InputTextarea } from "primereact/inputtextarea";
import fileUploadLogo from "../../../assets/upload-file.svg";
import noPreviewLogo from "../../../assets/no-preview.svg";
import { Button } from "primereact/button";
import PropTypes from "prop-types";
import Categories from "./utils/categories.json";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { format } from "date-fns";
import useFileUpload from "~/utils/useFileUpload";
import { removeToken, getAdHocEscalationDisplayName } from "~/utils/fileUtils";
import SearchBar from "~/components/searchBar";

const FileUploadModal = ({
  showModal,
  hideUploadModal,
  fileSupported,
  uploadedFile,
  setUploadedFile,
  getService,
  entitydata,
  employeeData,
  requestdata,
  backButton,
  placeholder,
  processdata = {},
  cancelButtonName,
  loading = false,
  close = true,
  isEmployeeAttachment = false,
  relatedType = "",
  shareholderdata = {},
  officerdata = {},
  directordata = {},
  categories = Categories,
  isOutOfScope = false,
  serviceLineName = null,
}) => {
  const [category, setCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [showCategories, setShowCategories] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [expandedCategoryCode, setExpandedCategoryCode] = useState(null);
  const [year, setYear] = useState(null);
  const [fileYearMonth, setFileYearMonth] = useState(null);
  const [fileYearMonthViewDate, setFileYearMonthViewDate] = useState(
    () => new Date(),
  );
  const [showFileYearMonthPicker, setShowFileYearMonthPicker] = useState(false);
  const fileUploadRef = useRef(null);
  const fileYearMonthPickerRef = useRef(null);

  const today = new Date();
  const minFileYearMonth = new Date(today.getFullYear() - 10, 0, 1);
  const maxFileYearMonth = new Date(today.getFullYear() + 10, 11, 31);
  const minFileYear = minFileYearMonth.getFullYear();
  const maxFileYear = maxFileYearMonth.getFullYear();
  const isPayrollServiceOnly =
    processdata?.serviceData?.name === "Payroll" ||
    requestdata?.serviceData?.name === "Payroll" ||
    serviceLineName === "Payroll";
  const showFileYearMonth = !isEmployeeAttachment && !isOutOfScope;
  const categoryRef = useRef(null);

  const {
    uuidToUlid,
    SERVICE_CODES,
    getFileVersion,
    getFormattedProcessName,
    REQUEST_REASON_CODES,
    isFetchingVersion,
  } = useFileUpload();

  const getFileSizeMB = (file) => (file.size / (1024 * 1024)).toFixed(2);

  const handleCategoryToggle = (cat) => {
    setExpandedCategoryCode((prev) => (prev === cat?.code ? null : cat?.code));
  };

  const handleSubCategorySelect = (parentCat, subOption) => {
    setCategory(parentCat);
    setSubCategory({ name: subOption.name, code: subOption.code });
    setShowCategories(false);
    setFilteredCategories(categories);
    setSelectedIndex(null);
    setExpandedCategoryCode(null);
    setYear(null);
  };

  const onFileUpload = (e) => {
    const uniqueFiles = e.files
      .filter((eventFile) => !files.includes(eventFile))
      .map((eventFile) => {
        if (
          !fileSupported.includes(eventFile.name.split(".").pop().toLowerCase())
        ) {
          return { file: eventFile, error: { state: true, visible: true } };
        } else {
          if (getFileSizeMB(eventFile) > 20) {
            return { file: eventFile, error: { state: true, visible: true } };
          } else {
            return { file: eventFile, error: { state: false, visible: false } };
          }
        }
      });

    setFiles([...files, ...uniqueFiles]);
    e.files = [];
    fileUploadRef.current.clear();
  };

  const onFileRemove = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const fileDropHandler = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      fileUploadRef.current.files = files;
      const newFiles = Array.from(files);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    } else {
      console.error("No files were dropped");
    }
  };

  const getFileType = (fileName) => {
    return fileName.split(".").pop();
  };

  const handleFileYearMonthChange = (e) => {
    const next = e.value;
    if (!next) {
      setFileYearMonth(null);
      return;
    }
    const year = next.getFullYear();
    if (year < minFileYear || year > maxFileYear) {
      return;
    }
    setFileYearMonth(next);
    setFileYearMonthViewDate(next);
    setShowFileYearMonthPicker(false);
  };

  const handleViewDateChange = (e) => {
    const next = e.value;
    if (!next) return;
    const year = next.getFullYear();
    if (year < minFileYear || year > maxFileYear) {
      return;
    }
    setFileYearMonthViewDate(next);
  };

  const handleReplace = (event, fileToReplace) => {
    const newFile = event.files[0];
    const newFiles = files.map((f) => {
      if (f.file === fileToReplace) {
        if (
          !fileSupported.includes(newFile.name.split(".").pop().toLowerCase())
        ) {
          return { file: newFile, error: { state: true, visible: true } };
        } else if (getFileSizeMB(newFile) > 20) {
          return { file: newFile, error: { state: true, visible: true } };
        } else {
          return { file: newFile, error: { state: false, visible: false } };
        }
      }
      return f;
    });
    setFiles(newFiles);
  };

  const getFileName = async (file) => {
    const fileType = getFileType(file?.name);
    const yearPart = year ? `_${year}` : "";
    let fileName = "";
    if (relatedType === "PROCESS") {
      const isPublic = location.pathname.includes("public");
      const tags = {
        category: file?.category,
        subCategory: file?.subcategory,
        entity_id: uuidToUlid(processdata?.entity),
        service: SERVICE_CODES[getService()?.name],
        is_public: isPublic === true ? "true" : "false",
        country: processdata?.entityData.country,
        process: uuidToUlid(processdata?.id),
        year: new Date().getFullYear().toString(),
        temporary_file: "false",
      };
      let version = `${await getFileVersion(tags)}`;
      files[0]["file"]["versionNumber"] = Number(version);
      if (uploadedFile?.length > 0) {
        const { category, subcategory } = file;
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === category &&
            item.file.subcategory === subcategory,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${getFormattedProcessName(processdata)}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "ENTITIES_PLUS") {
      const tags = {
        entity_id: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        core_entity_info: "Core Entity Information",
        temporary_file: "false",
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "SHAREHOLDER") {
      const tags = {
        entity_id: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        temporary_file: "false",
        shareHolder: uuidToUlid(shareholderdata?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "DIRECTOR") {
      const tags = {
        entity_id: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        temporary_file: "false",
        director: uuidToUlid(directordata?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "OFFICER") {
      const tags = {
        entity_id: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        temporary_file: "false",
        officer: uuidToUlid(officerdata?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "REQUEST") {
      const subCategoryCode =
        typeof file?.subcategory === "object"
          ? file.subcategory?.code
          : file?.subcategory;
      const tags = {
        entity_id: uuidToUlid(entitydata.id),
        service: SERVICE_CODES[requestdata?.serviceData?.name],
        country: entitydata.country,
        request: uuidToUlid(requestdata?.id),
        request_reason: REQUEST_REASON_CODES[requestdata?.reason],
        year: new Date().getFullYear().toString(),
        category: file?.category,
        subCategory: subCategoryCode,
        temporary_file: "false",
      };
      let version = `${await getFileVersion(tags)}`;
      files[0]["file"]["versionNumber"] = Number(version);
      if (uploadedFile?.length > 0) {
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === file?.category &&
            (typeof item.file.subcategory === "object"
              ? item.file.subcategory?.code
              : item.file.subcategory) === subCategoryCode,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${requestdata?.reason}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (
      relatedType === "SHAREHOLDER_ONBOARDING" ||
      relatedType === "OFFICER_ONBOARDING"
    ) {
      let version = 1;
      files[0]["file"]["versionNumber"] = version;
      if (uploadedFile?.length > 0) {
        const { category, subcategory } = file;
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === category &&
            item.file.subcategory === subcategory,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "AD_HOC" || relatedType === "ESCALATION") {
      const subCategoryCode =
        typeof file?.subcategory === "object"
          ? file.subcategory?.code
          : file?.subcategory;
      let version = 1;
      files[0]["file"]["versionNumber"] = version;
      if (uploadedFile?.length > 0) {
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === file?.category &&
            (typeof item.file.subcategory === "object"
              ? item.file.subcategory?.code
              : item.file.subcategory) === subCategoryCode,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.reason}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (isEmployeeAttachment && employeeData) {
      const tags = {
        entity_id: uuidToUlid(employeeData?.entity_id),
        country: entitydata?.country,
        temporary_file: "false",
        employee: uuidToUlid(employeeData?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
        ...(file?.customYear && { customYear: file?.customYear.toString() }),
      };
      removeToken();
      const container =
        file?.subcategory === "TAX_STATEMENTS" ? "ess" : "documents";
      const version = `${await getFileVersion(tags, container)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${employeeData.employee_identifier}_${employeeData.last_name}${yearPart}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    }

    return fileName;
  };

  const uploadFile = async () => {
    files[0]["file"]["category"] = category.code;
    const isAdHocOrEscalation =
      relatedType === "REQUEST" ||
      relatedType === "AD_HOC" ||
      relatedType === "ESCALATION";
    files[0]["file"]["subcategory"] = isAdHocOrEscalation
      ? { code: subCategory.code, name: subCategory.name }
      : subCategory.code;
    if (!isAdHocOrEscalation) {
      files[0]["file"]["subcategoryName"] = subCategory.name;
    }
    if (description) {
      files[0]["file"]["description"] = description;
    }
    if (year) {
      files[0]["file"]["customYear"] = year;
    }
    if (fileYearMonth) {
      const d = new Date(fileYearMonth);
      files[0]["file"]["fileYearMonth"] = {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
      };
    }
    files[0]["file"]["fileName"] = await getFileName(files[0]["file"]);
    await setUploadedFile(files);
    if (close) {
      hideUploadModal();
    }
  };
  useEffect(() => {
    clear();
  }, [showModal]);
  const descriptionHandler = (event) => {
    setDescription(event.target.value);
  };

  const onUpdateVisibility = (fileToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file === fileToRemove
          ? { ...file, error: { state: true, visible: false } }
          : file,
      ),
    );
  };

  const clear = () => {
    setCategory(null);
    setFiles([]);
    setSubCategory(null);
    setShowCategories(false);
    setYear(null);
    setFileYearMonth(null);
    setDescription("");
    fileUploadRef?.current?.clear();
    setSelectedIndex(null);
    setFilteredCategories(categories);
    setExpandedCategoryCode(null);
  };
  const handleCloseModal = () => {
    hideUploadModal();
    clear();
  };

  const searchEntity = (value) => {
    if (!value) setFilteredCategories(categories);
    const filtered = categories.filter((cat) => {
      return cat?.name.toLowerCase().includes(value.toLowerCase());
    });
    setFilteredCategories(filtered);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOutsideCategory =
        categoryRef.current && !categoryRef.current.contains(event.target);
      const isClickOutsideFileYearMonth =
        fileYearMonthPickerRef.current &&
        !fileYearMonthPickerRef.current.contains(event.target);

      if (showCategories && isClickOutsideCategory) {
        setShowCategories(false);
        setExpandedCategoryCode(null);
      }

      if (showFileYearMonthPicker && isClickOutsideFileYearMonth) {
        setShowFileYearMonthPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCategories, showFileYearMonthPicker]);

  const footer = () => {
    return (
      <div className="footer-btn-container">
        <CustomeButton
          label={cancelButtonName ? cancelButtonName : "Cancel"}
          variant="outlined"
          className="cancel-button"
          onClick={() => {
            handleCloseModal();
          }}
        />
        <CustomeButton
          label="Upload File"
          variant="contained"
          className="upload-file-btn"
          disabled={
            loading ||
            !category ||
            !subCategory ||
            (isEmployeeAttachment &&
              category?.code === "PAYSLIPS_AND_PAYROLL_REPORTS" &&
              subCategory?.code === "TAX_STATEMENTS" &&
              !year) ||
            files.length < 1 ||
            files[0]?.error?.state ||
            files[0]?.error?.visible ||
            isFetchingVersion
          }
          onClick={uploadFile}
          loading={loading || isFetchingVersion}
          iconPos="right"
        />
      </div>
    );
  };

  return (
    <Dialog
      header={
        <div className="flex justify-content-between align-items-center">
          <div className="flex align-items-center">
            {backButton && (
              <i
                className="pi pi-arrow-left back-icon ml-3"
                onClick={handleCloseModal}
                style={{ cursor: "pointer" }}
              ></i>
            )}
            <span className="model-head">Upload File</span>
          </div>
          <i
            className="pi pi-times cross-icon"
            onClick={handleCloseModal}
            style={{ cursor: "pointer" }}
          ></i>
        </div>
      }
      visible={showModal}
      className="file-upload-dialog"
      onHide={() => {
        handleCloseModal();
      }}
      footer={footer}
    >
      <div className="file-upload-container" onDrop={() => fileDropHandler()}>
        <div className="file-upload-section">
          <div
            className={`file-drag-drop-section ${files.length > 0 ? "no-preview" : ""}`}
          >
            <img
              src={files.length === 0 ? fileUploadLogo : noPreviewLogo}
              alt="file-upload"
            />
            {files.length === 0 ? (
              <p className="drag-drop-text">
                {placeholder
                  ? placeholder
                  : "Click anywhere to select and upload a file."}
              </p>
            ) : (
              <p className="no-preview-text">No Preview Available</p>
            )}
            {files.length === 0 && (
              <FileUpload
                ref={fileUploadRef}
                name="files"
                customUpload={true}
                onSelect={onFileUpload}
                chooseOptions={{
                  icon: "pi pi-upload",
                  label: "Select File",
                  iconOnly: false,
                  className: "select-file-btn",
                }}
                mode="advanced"
              />
            )}
          </div>
          <div className="file-format-section">
            <p className="format-text">
              Supported formats: PDF, DOC, CSV, XLS, JPEG, JPG, PNG, TXT, XML
            </p>
            <p className="file-size-text">Maximum file size: 20MB</p>
          </div>

          {files.length > 0 && (
            <div className="attachment-layout">
              <div className="attachment-container">
                {files.map((file, index) => {
                  const displayName = isPayrollServiceOnly
                    ? file.file?.name
                    : category && subCategory
                      ? getAdHocEscalationDisplayName(
                          subCategory.name,
                          fileYearMonth,
                          file.file?.name,
                        )
                      : undefined;
                  return (
                    <Attachments
                      file={file.file}
                      key={index}
                      fileHandler={() => {
                        onFileRemove(file);
                      }}
                      handleReplace={(e) => handleReplace(e, file.file)}
                      showReplaceButton={true}
                      icon="pi-times"
                      error={file.error}
                      getFileSizeMB={getFileSizeMB}
                      handleErrorVisibility={() => {
                        onUpdateVisibility(file);
                      }}
                      displayName={displayName}
                      useUploadActions={true}
                      showRemoveIconInsteadOfDownload={true}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="details-section">
          <p className="details-title"> File Details</p>
          <div className="details-field-container">
            <p className="category-label">Category*</p>
            <Button
              outlined
              icon="pi pi-angle-down"
              className="category-dropdown-btn"
              placeholder="Select Category"
              onClick={() => setShowCategories(true)}
            >
              <span className="category-selected">
                {subCategory?.name || "Select Category"}
              </span>
            </Button>

            {showCategories && (
              <div
                className="category-container category-tree-container"
                ref={categoryRef}
              >
                <div className="search-container">
                  <SearchBar onChange={(e) => searchEntity(e.target.value)} />
                </div>

                <ul className="category-tree">
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((cat, index) => (
                      <li
                        key={cat?.code ?? index}
                        className="category-tree-node"
                      >
                        <div
                          className={`category-tree-parent ${expandedCategoryCode === cat?.code ? "expanded" : ""}`}
                          onClick={() => handleCategoryToggle(cat)}
                        >
                          <i
                            className={`pi ${expandedCategoryCode === cat?.code ? "pi-chevron-down" : "pi-chevron-right"}`}
                          />
                          <i
                            className={`pi ${expandedCategoryCode === cat?.code ? "pi-folder-open" : "pi-folder"}`}
                          />
                          <span>{cat?.name}</span>
                        </div>
                        {expandedCategoryCode === cat?.code && (
                          <ul className="category-tree-children">
                            {cat?.subCategory?.map((sub, subIndex) => (
                              <li
                                key={sub?.code ?? subIndex}
                                className={`category-tree-child ${subCategory?.code === sub?.code ? "selected" : ""}`}
                                onClick={() =>
                                  handleSubCategorySelect(cat, sub)
                                }
                              >
                                <i className="pi pi-folder" />
                                <span>{sub?.name}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))
                  ) : (
                    <p className="empty-search">No results found</p>
                  )}
                </ul>
              </div>
            )}
            {isEmployeeAttachment &&
              category?.code === "PAYSLIPS_AND_PAYROLL_REPORTS" &&
              subCategory?.code === "TAX_STATEMENTS" && (
                <div className="details-field-container">
                  <p className="description-label">Year*</p>
                  <InputNumber
                    value={year}
                    placeholder="Enter Year"
                    onChange={(e) => {
                      const rawValue = e.value;
                      const numericValue = parseInt(rawValue, 10);
                      setYear(isNaN(numericValue) ? null : numericValue);
                    }}
                    useGrouping={false}
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    min={0}
                    mode="decimal"
                  />
                </div>
              )}
          </div>
          {showFileYearMonth && (
            <div
              className="details-field-container file-year-month-field"
              ref={fileYearMonthPickerRef}
            >
              <p className="description-label">File Year &amp; Month</p>
              <Button
                outlined
                icon="pi pi-angle-down"
                iconPos="right"
                className="category-dropdown-btn file-year-month-btn"
                onClick={() => setShowFileYearMonthPicker((prev) => !prev)}
              >
                <span className="category-selected">
                  {fileYearMonth
                    ? format(fileYearMonth, "MMM yyyy")
                    : "Select date"}
                </span>
              </Button>
              {showFileYearMonthPicker && (
                <div className="file-year-month-container">
                  <Calendar
                    value={fileYearMonth}
                    onChange={handleFileYearMonthChange}
                    view="month"
                    inline
                    minDate={minFileYearMonth}
                    maxDate={maxFileYearMonth}
                    viewDate={fileYearMonthViewDate}
                    onViewDateChange={handleViewDateChange}
                    className={`file-year-month-calendar-inline ${
                      fileYearMonthViewDate.getFullYear() <= minFileYear
                        ? "file-year-month-min-year"
                        : ""
                    } ${
                      fileYearMonthViewDate.getFullYear() >= maxFileYear
                        ? "file-year-month-max-year"
                        : ""
                    }`}
                  />
                </div>
              )}
            </div>
          )}
          <div className="details-field-container">
            <p className="description-label">Description (Optional)</p>
            <InputTextarea
              value={description}
              onChange={descriptionHandler}
              placeholder="Describe the file you are uploading"
              autoResize={true}
              maxLength={200}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

FileUploadModal.propTypes = {
  showModal: PropTypes.bool,
  hideUploadModal: PropTypes.func,
  fileSupported: PropTypes.array,
  setUploadedFile: PropTypes.func,
  categories: PropTypes.array,
  isOutOfScope: PropTypes.bool,
  serviceLineName: PropTypes.string,
};

export default FileUploadModal;
